import { useState, useEffect, useCallback } from "react";
import { api } from "@/services/api";

interface User { id: number; first_name: string; last_name: string; email: string; phone: string; role: string; role_name: string; is_active: number; created_at: string }
interface Role { id: number; name: string; slug: string }
interface ListResponse { data: User[]; total: number; page: number; limit: number }

export default function AdminUsers() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams();
      qs.set("page", String(page));
      qs.set("limit", "15");
      if (search.trim()) qs.set("search", search.trim());
      if (roleFilter) qs.set("role", roleFilter);
      const [res, r] = await Promise.all([
        api.get<ListResponse>(`/users?${qs.toString()}`),
        api.get<Role[]>("/users/roles"),
      ]);
      setData(res);
      setRoles(r);
    } catch (e: any) {
      setError(e?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter]);

  useEffect(() => { load(); }, [load]);

  async function toggleActive(u: User) {
    try {
      await api.put(`/users/${u.id}`, { is_active: u.is_active ? 0 : 1 });
      load();
    } catch (e: any) {
      alert(e?.message || "Update failed");
    }
  }

  const pageCount = data ? Math.max(Math.ceil(data.total / data.limit), 1) : 1;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-foreground-600">{data?.total ?? 0} team members</p>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 flex items-center">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search name or email"
              className="w-full sm:w-60 rounded-full border border-background-300 bg-background-50 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
            className="rounded-full border border-background-300 bg-background-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <option value="">All roles</option>
            {roles.map((r) => <option key={r.id} value={r.slug}>{r.name}</option>)}
          </select>
          <button onClick={() => setCreateOpen(true)} className="inline-flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700">
            <i className="ri-user-add-line"></i> Add User
          </button>
        </div>
      </div>

      {error && <div className="rounded-2xl bg-accent-100 border border-accent-300 p-4 text-sm text-accent-900">{error}</div>}

      <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-100 text-left text-xs uppercase tracking-wider text-foreground-600">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {loading && (<tr><td colSpan={5} className="px-4 py-10 text-center text-foreground-600"><i className="ri-loader-4-line animate-spin"></i> Loading...</td></tr>)}
              {!loading && data?.data.map((u) => (
                <tr key={u.id} className="hover:bg-background-100/60">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-600 text-background-50 text-xs font-bold">
                        {(u.first_name || "A")[0]}{(u.last_name || "U")[0]}
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground-950">{u.first_name} {u.last_name}</p>
                        <p className="text-xs text-foreground-600 truncate">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 capitalize text-foreground-800">{u.role_name || u.role}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleActive(u)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${u.is_active ? "bg-primary-100 text-primary-900" : "bg-background-200 text-foreground-700"}`}>
                      {u.is_active ? "Active" : "Disabled"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-foreground-700">{new Date(u.created_at + "Z").toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => setEditUser(u)} className="inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-700">
                        <i className="ri-edit-line"></i> Edit
                      </button>
                      <button onClick={() => toggleActive(u)} className="inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-accent-700">
                        <i className={u.is_active ? "ri-eye-off-line" : "ri-eye-line"}></i>
                        {u.is_active ? "Disable" : "Enable"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && data?.data.length === 0 && (<tr><td colSpan={5} className="px-4 py-12 text-center text-foreground-600">No users found.</td></tr>)}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-background-100">
          <p className="text-xs text-foreground-600">Total: {data?.total ?? 0}</p>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page <= 1} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100 disabled:opacity-40"><i className="ri-arrow-left-s-line"></i></button>
            <span className="px-3 text-sm text-foreground-700">{page} / {pageCount}</span>
            <button onClick={() => setPage((p) => Math.min(p + 1, pageCount))} disabled={page >= pageCount} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100 disabled:opacity-40"><i className="ri-arrow-right-s-line"></i></button>
          </div>
        </div>
      </div>

      {createOpen && <CreateUser roles={roles} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />}
      {editUser && <EditUser user={editUser} roles={roles} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} />}
    </div>
  );
}

function CreateUser({ roles, onClose, onCreated }: { roles: Role[]; onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", role_id: roles[0]?.id || "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.post("/users", form);
      onCreated();
    } catch (err: any) {
      setError(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-3xl bg-background-50 border border-background-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-100">
          <h3 className="font-heading text-lg text-foreground-950">Add User</h3>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100" aria-label="Close"><i className="ri-close-line"></i></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {error && <div className="rounded-lg bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-900">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-800 mb-1.5">First Name *</label>
              <input required value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Last Name</label>
              <input value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Email *</label>
            <input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Password *</label>
            <input required type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Role</label>
            <select value={form.role_id} onChange={(e) => setForm((f) => ({ ...f, role_id: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400">
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-background-300 text-foreground-800 px-5 py-2.5 hover:bg-background-100">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700 disabled:opacity-60">
              {saving ? <><i className="ri-loader-4-line animate-spin"></i> Saving...</> : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUser({ user, roles, onClose, onSaved }: { user: User; roles: Role[]; onClose: () => void; onSaved: () => void }) {
  const presetRoleId = roles.find((r) => r.slug === user.role)?.id ?? roles[0]?.id ?? "";
  const [form, setForm] = useState({
    first_name: user.first_name || "",
    last_name: user.last_name || "",
    phone: user.phone || "",
    role_id: String(presetRoleId),
    is_active: !!user.is_active,
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload: Record<string, unknown> = {
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        role_id: form.role_id ? Number(form.role_id) : null,
        is_active: form.is_active ? 1 : 0,
      };
      if (form.password) payload.password = form.password;
      await api.put(`/users/${user.id}`, payload);
      onSaved();
    } catch (err: any) {
      setError(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-3xl bg-background-50 border border-background-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-100">
          <h3 className="font-heading text-lg text-foreground-950">Edit User</h3>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100" aria-label="Close"><i className="ri-close-line"></i></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {error && <div className="rounded-lg bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-900">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-800 mb-1.5">First Name *</label>
              <input required value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Last Name</label>
              <input value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Phone</label>
            <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Role</label>
            <select value={form.role_id} onChange={(e) => setForm((f) => ({ ...f, role_id: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400">
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">New password (leave blank to keep current)</label>
            <input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Min 8 characters" className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4 rounded accent-primary-600" />
            <span className="text-sm text-foreground-800">Account active</span>
          </label>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-background-300 text-foreground-800 px-5 py-2.5 hover:bg-background-100">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700 disabled:opacity-60">
              {saving ? <><i className="ri-loader-4-line animate-spin"></i> Saving...</> : <><i className="ri-save-line"></i> Save Changes</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}